/**
 * Configuración centralizada de la aplicación
 * Todas las constantes y configuraciones en un solo lugar
 */

// === CONFIGURACIÓN DE API ===
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  TIMEOUT: {
    DEFAULT: parseInt(process.env.REACT_APP_EXTRACTION_TIMEOUT_DEFAULT, 10) || 300000,
    ZIP: parseInt(process.env.REACT_APP_EXTRACTION_TIMEOUT_ZIP, 10) || 900000,
  },
};

// === CONFIGURACIÓN DE CUOTAS ===
// Configuración base con valores por defecto (se actualizará dinámicamente desde el servidor)
const BASE_QUOTA_THRESHOLDS = {
  CRITICAL: 10, // Valor por defecto, se actualizará con servidor
  LOW: 50,      // Valor por defecto, se actualizará con servidor
};

// Configuración que puede ser actualizada dinámicamente
export let QUOTA_THRESHOLDS = { ...BASE_QUOTA_THRESHOLDS };

// === CONFIGURACIÓN DE ARCHIVOS ===
// Configuración base con valores por defecto (se actualizará dinámicamente desde el servidor)
const BASE_FILE_CONFIG = {
  MAX_FILES: 50, // Valor por defecto, se actualizará con servidor
  MAX_SIZE_MB: 50, // Valor por defecto, se actualizará con servidor
  MAX_SIZE_ZIP_MB: 200, // Valor por defecto para ZIP, se actualizará con servidor
  ALLOWED_TYPES: {
    'image/png': true,
    'image/jpeg': true,
    'image/jpg': true,
    'application/pdf': true,
    'application/zip': true,
    'application/x-zip-compressed': true,
  },
  ICONS: {
    'image/png': 'bi-file-earmark-image text-info',
    'image/jpeg': 'bi-file-earmark-image text-info',
    'image/jpg': 'bi-file-earmark-image text-info',
    'application/pdf': 'bi-file-earmark-pdf text-danger',
    'application/zip': 'bi-file-earmark-zip text-warning',
    'application/x-zip-compressed': 'bi-file-earmark-zip text-warning',
  },
};

// Configuración que puede ser actualizada dinámicamente
export let FILE_CONFIG = { ...BASE_FILE_CONFIG };

/**
 * Actualizar configuración de archivos desde el servidor
 * @param {Object} serverConfig - Configuración del servidor
 */
export const updateFileConfig = (serverConfig) => {
  if (serverConfig) {
    FILE_CONFIG = {
      ...BASE_FILE_CONFIG,
      MAX_FILES: serverConfig.max_files_per_extraction || BASE_FILE_CONFIG.MAX_FILES,
      MAX_SIZE_MB: serverConfig.max_file_size_mb || BASE_FILE_CONFIG.MAX_SIZE_MB,
      MAX_SIZE_ZIP_MB: serverConfig.max_file_size_zip_mb || BASE_FILE_CONFIG.MAX_SIZE_ZIP_MB,
    };
    
    // Actualizar también los umbrales de cuota si están disponibles
    if (serverConfig.quota_thresholds) {
      QUOTA_THRESHOLDS = {
        CRITICAL: serverConfig.quota_thresholds.critical || BASE_QUOTA_THRESHOLDS.CRITICAL,
        LOW: serverConfig.quota_thresholds.low || BASE_QUOTA_THRESHOLDS.LOW,
      };
    }
  }
};

/**
 * Obtener configuración actual de archivos
 * @returns {Object} Configuración actual
 */
export const getFileConfig = () => FILE_CONFIG;

/**
 * Obtener umbrales de cuota actuales
 * @returns {Object} Umbrales de cuota actuales
 */
export const getQuotaThresholds = () => QUOTA_THRESHOLDS;

// === CONFIGURACIÓN DE EXPORTACIÓN ===
export const EXPORT_CONFIG = {
  FORMATS: ['xlsx', 'csv', 'json'],
  DEFAULT_FORMAT: 'xlsx',
};

// === FUNCIONES UTILITARIAS ===

// Funciones utilitarias para evaluar el estado de la cuota
export const getQuotaStatus = (availableDocuments) => {
  const thresholds = getQuotaThresholds();
  
  if (availableDocuments <= thresholds.CRITICAL) {
    return {
      status: 'critical',
      label: 'Cuota Crítica',
      message: 'Agregar documentos urgentemente',
      color: 'danger',
      icon: 'bi-exclamation-octagon-fill'
    };
  }
  
  if (availableDocuments <= thresholds.LOW) {
    return {
      status: 'low',
      label: 'Cuota Baja',
      message: 'Considera agregar más documentos',
      color: 'warning',
      icon: 'bi-exclamation-triangle-fill'
    };
  }
  
  return {
    status: 'healthy',
    label: 'Cuota Saludable',
    message: 'Usuario con suficientes documentos',
    color: 'success',
    icon: 'bi-check-circle-fill'
  };
};

// Función para obtener clases CSS basadas en el estado
export const getQuotaClasses = (availableDocuments) => {
  const status = getQuotaStatus(availableDocuments);
  
  return {
    alert: `alert-${status.color}`,
    text: `text-${status.color}`,
    bg: `bg-${status.color}`,
    btn: `btn-${status.color}`,
    btnOutline: `btn-outline-${status.color}`
  };
};

// Validar tipo de archivo
export const validateFileType = (file) => {
  return getFileConfig().ALLOWED_TYPES[file.type] || false;
};

// Validar tamaño de archivo según el tipo
export const validateFileSize = (file) => {
  const config = getFileConfig();
  const isZip = file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
  const maxSizeMB = isZip ? config.MAX_SIZE_ZIP_MB : config.MAX_SIZE_MB;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  return {
    isValid: file.size <= maxSizeBytes,
    maxSizeMB: maxSizeMB,
    actualSizeMB: Math.round(file.size / (1024 * 1024) * 100) / 100,
    isZip: isZip
  };
};

// Obtener límite de tamaño para un tipo de archivo específico
export const getMaxSizeForFile = (file) => {
  const config = getFileConfig();
  const isZip = file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
  return isZip ? config.MAX_SIZE_ZIP_MB : config.MAX_SIZE_MB;
};

// Obtener icono de archivo
export const getFileIcon = (fileType) => {
  return getFileConfig().ICONS[fileType] || 'bi-file-earmark text-secondary';
};

// Formatear tamaño de archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
